import http from "@http";
import config from "@config";
import actionCreators from "@modules/client/actionCreator";
import contextActionCreators from "@modules/context/actionCreator";
import employeeActionCreators from "@modules/employee/actionCreator";
import { toast } from "react-toastify";
import { getClientFilters, getClientsSort } from "@modules/client/selectors";
import { getEmployeeFilters } from "@modules/employee/selectors";
import { getFormValues } from "redux-form";
import {
  AddClientDetails,
  ConfirmDisableAdminAccount,
  DisableAdminAccount,
  GetAdmins,
  SetAdminsSort,
  GetAdminSchema,
  GetClientChangeLog,
  GetClients,
  GetClientsCsv,
  GetClientsCsvSuccess,
  SetClientsSort,
  UpdateClientDetails,
  CreateAdminAccount,
  EnableAdminAccount,
  GetAllClientsList,
} from "@modules/client/constants/actions";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import { downloadCsvFile } from "@sagas/utils";
import { all, call, put, takeEvery, select, take, spawn } from "redux-saga/effects";
import { transformFilters, OPERATORS } from "@transformers/request";
import { sortToString } from "@transformers/sort";
import moment from "moment";

import { getSchema } from "@utils/schema";

import clientSagas from "./client";
import clientSlaSagas from "./sla";
import clientServiceSagas from "./service-scope";
import { USER_TYPES } from "@modules/account/constants";

export function* onGetClients({ payload }) {
  const filters = yield select(getClientFilters);
  const sort = yield select(getClientsSort);
  const additionalFilters = (payload || {}).filters || {};
  const page = (payload || {}).page || 1;

  try {
    const response = yield call(http.get, `${config.endpointBaseUrl}api/v1/client-admin/client`, {
      params: {
        search: transformFilters(Object.assign({}, filters, additionalFilters)),
        page,
        sort: sortToString(sort),
        limit: config.results100PerPage,
      },
    });

    yield put(actionCreators.getClientsSuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.getClientsError(e.response));
  }
}

export function* onGetClientChangeLog({ payload }) {
  const page = payload.page || 1;
  const clientId = payload.clientId || null;

  try {
    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/client-admin/client/${clientId}/changelog`,
      {
        params: {
          page,
          limit: config.resultsPerPage,
        },
      },
    );
    yield put(actionCreators.getClientChangeLogSuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.getClientChangeLogError(e.response));
  }
}

export function* onGetClientsCsv() {
  const filters = yield select(getClientFilters);
  try {
    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/client-admin/client/export`,
      {
        params: {
          search: transformFilters(filters),
        },
        headers: {
          Accept: "text/csv",
        },
      },
    );

    yield put(actionCreators.getClientsCsvSuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.getClientsCsvError(e.response));
  }
}

export function* onGetClientsCsvSuccess({ payload }) {
  yield downloadCsvFile(payload, "clients.csv");
}

export function* onGetAdminsSchema() {
  try {
    const schema = getSchema("ClientUserDTO");
    yield put(actionCreators.setAdminSchema(schema));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.getAdminsError(e.response));
  }
}

export function* onUpdateClientDetails({ payload }) {
  try {
    const clientId = payload.id || null;

    const response = yield call(
      http.put,
      `${config.endpointBaseUrl}api/v1/client-admin/client/${clientId}`,
      payload,
    );
    yield all([
      put(actionCreators.UpdateClientDetailsSuccess(response.data)),
      put(actionCreators.getClient({ clientId })),
      put(contextActionCreators.GetClientContext({ clientId })),
      put(actionCreators.GetClientChangeLog({ clientId })),
      put(actionCreators.toggleEditClientModal(false)),
      put(sendSuccessMessage("CLIENT_UPDATED")),
    ]);
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.UpdateClientDetailsError(e.response));
  }
}

export function* onAddClientDetails({ payload }) {
  try {
    yield call(http.post, `${config.endpointBaseUrl}api/v1/client-admin/client`, payload);
    yield all([
      put(actionCreators.GetClients(payload)),
      put(actionCreators.AddClientDetailsSuccess()),
      put(actionCreators.ToggleAddClientModal()),
      put(sendSuccessMessage("CLIENT_CREATED")),
    ]);
  } catch (e) {
    console.error("Saga error: ", e);
    yield all([
      put(actionCreators.AddClientDetailsError(e.response)),
      put(actionCreators.ToggleAddClientModal()),
    ]);
  }
}

function* onGetAllClientsList(): Generator<*, void, *> {
  try {
    const resp = yield call(http.get, `${config.endpointBaseUrl}api/v1/client-admin/client`);
    yield put(actionCreators.GetAllClientsListSuccess(resp.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.GetAllClientsListError(e.response));
  }
}

export function* ClientSaga() {
  yield all([
    // Client List
    takeEvery(AddClientDetails, onAddClientDetails),
    takeEvery(GetClients, onGetClients),
    takeEvery(GetClientsCsv, onGetClientsCsv),
    takeEvery(GetClientsCsvSuccess, onGetClientsCsvSuccess),
    takeEvery(SetClientsSort, onGetClients),
    // Client Services
    spawn(clientServiceSagas),
    // Client Details
    takeEvery(UpdateClientDetails, onUpdateClientDetails),
    // Changelog
    takeEvery(GetClientChangeLog, onGetClientChangeLog),
    // Client Admin
    takeEvery(GetAdminSchema, onGetAdminsSchema),
    // SLA
    spawn(clientSlaSagas),
    takeEvery(GetAllClientsList, onGetAllClientsList),
    // Client
    spawn(clientSagas),
  ]);
}

export default ClientSaga;
